import { brandStorage as localStorage } from '@/utils/brandSession';
/**
 * Dashboard Anomalie Questura
 * Rileva e gestisce anomalie nelle dichiarazioni AlloggiatiWeb:
 * - Dichiarazioni esterne (non dal sistema)
 * - Doppi invii
 * - Ricevute mancanti
 * Visibile solo a: Admin, Customer Service
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertTriangle, Shield, ShieldAlert, ShieldCheck,
  Search, RefreshCw, CheckCircle, XCircle,
  Eye, FileText, Clock, Building, Calendar,
  Loader2, ArrowUpDown, ChevronLeft, ChevronRight,
  Mail, Scan
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { API } from '../../utils/config';

const ANOMALY_TYPE_LABELS = {
  external_declaration: 'Dichiarazione Esterna',
  duplicate_submission: 'Doppio Invio',
  missing_receipt: 'Ricevuta Mancante'
};

const ANOMALY_TYPE_COLORS = {
  external_declaration: 'bg-red-100 text-red-800 border-red-200',
  duplicate_submission: 'bg-amber-100 text-amber-800 border-amber-200',
  missing_receipt: 'bg-blue-100 text-blue-800 border-blue-200'
};

const ANOMALY_TYPE_ICONS = {
  external_declaration: ShieldAlert,
  duplicate_submission: FileText,
  missing_receipt: Clock
};

const STATUS_LABELS = {
  open: 'Aperta',
  resolved: 'Risolta',
  false_positive: 'Falso Positivo'
};

const STATUS_COLORS = {
  open: 'bg-red-100 text-red-800',
  resolved: 'bg-green-100 text-green-800',
  false_positive: 'bg-gray-100 text-gray-600'
};

const QuesturaAnomalyDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [anomalies, setAnomalies] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [scanStatus, setScanStatus] = useState(null);

  // Active tab
  const [activeTab, setActiveTab] = useState('anomalies'); // 'anomalies' | 'compliance'

  // Compliance data
  const [complianceLogs, setComplianceLogs] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);
  const [complianceTotal, setComplianceTotal] = useState(0);
  const [compliancePage, setCompliancePage] = useState(0);
  const [triggeringCheck, setTriggeringCheck] = useState(false);

  // Filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');

  // Resolve dialog
  const [resolveDialog, setResolveDialog] = useState(false);
  const [selectedAnomaly, setSelectedAnomaly] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolveStatus, setResolveStatus] = useState('resolved');
  const [resolving, setResolving] = useState(false);

  // Scan config dialog
  const [scanDialog, setScanDialog] = useState(false);
  const [scanDaysBack, setScanDaysBack] = useState(30);
  const [scanExternal, setScanExternal] = useState(false);

  const token = localStorage.getItem('hw_token');
  const LIMIT = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (typeFilter !== 'all') params.append('anomaly_type', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', LIMIT);
      params.append('skip', page * LIMIT);

      const [anomaliesRes, statsRes] = await Promise.all([
        fetch(`${API}/api/questura-anomalies?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/questura-anomalies/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (anomaliesRes.ok) {
        const data = await anomaliesRes.json();
        setAnomalies(data.anomalies || []);
        setTotal(data.total || 0);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching anomalies:', error);
      toast.error('Errore nel caricamento anomalie');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, page, token]);

  const fetchComplianceData = useCallback(async () => {
    try {
      const [logsRes, statsRes] = await Promise.all([
        fetch(`${API}/api/questura-compliance/logs?limit=${LIMIT}&skip=${compliancePage * LIMIT}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/api/questura-compliance/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      if (logsRes.ok) {
        const data = await logsRes.json();
        setComplianceLogs(data.logs || []);
        setComplianceTotal(data.total || 0);
      }
      if (statsRes.ok) {
        setComplianceStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    }
  }, [compliancePage, token]);

  const triggerComplianceCheck = async () => {
    setTriggeringCheck(true);
    try {
      const res = await fetch(`${API}/api/questura-compliance/run-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Controllo compliance avviato');
        setTimeout(() => fetchComplianceData(), 5000);
      }
    } catch (error) {
      toast.error('Errore avvio controllo');
    } finally {
      setTriggeringCheck(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'compliance') fetchComplianceData();
  }, [activeTab, fetchComplianceData]);

  // Poll scan status
  useEffect(() => {
    if (!scanId) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/questura-anomalies/scan-status/${scanId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setScanStatus(data);
          if (data.status === 'completed' || data.status === 'error') {
            setScanning(false);
            clearInterval(interval);
            fetchData();
            if (data.status === 'completed') {
              toast.success(`Scansione completata! ${data.total_anomalies || 0} anomalie trovate`);
            } else {
              toast.error('Scansione terminata con errore');
            }
          }
        }
      } catch (e) {
        // ignore
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [scanId, token, fetchData]);

  const startScan = async () => {
    setScanning(true);
    setScanDialog(false);
    try {
      const params = new URLSearchParams();
      params.append('days_back', scanDaysBack);
      params.append('scan_external', scanExternal);

      const res = await fetch(`${API}/api/questura-anomalies/scan?${params}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setScanId(data.scan_id);
        toast.info('Scansione avviata in background...');
      } else {
        throw new Error('Scan failed');
      }
    } catch (error) {
      setScanning(false);
      toast.error('Errore avvio scansione');
    }
  };

  const handleResolve = async () => {
    if (!selectedAnomaly) return;
    setResolving(true);
    try {
      const res = await fetch(`${API}/api/questura-anomalies/${selectedAnomaly.id}/resolve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: resolveStatus,
          resolution_note: resolutionNote
        })
      });
      if (res.ok) {
        toast.success('Anomalia aggiornata');
        setResolveDialog(false);
        setSelectedAnomaly(null);
        setResolutionNote('');
        fetchData();
      } else {
        throw new Error('Resolve failed');
      }
    } catch (error) {
      toast.error('Errore aggiornamento anomalia');
    } finally {
      setResolving(false);
    }
  };

  const filteredAnomalies = anomalies.filter(a => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (a.property_code || '').toLowerCase().includes(term) ||
      (a.property_name || '').toLowerCase().includes(term) ||
      (a.description || '').toLowerCase().includes(term) ||
      (a.details?.guest_name || '').toLowerCase().includes(term) ||
      (a.details?.booking_code || '').toLowerCase().includes(term)
    );
  });

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-6" data-testid="questura-anomaly-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="anomaly-title">
              Controllo Anomalie Questura
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Rileva dichiarazioni esterne, doppi invii e ricevute mancanti
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => fetchData()}
              variant="outline"
              size="sm"
              data-testid="refresh-anomalies-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
            </Button>
            <Button
              onClick={() => setScanDialog(true)}
              disabled={scanning}
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="start-scan-btn"
            >
              {scanning ? (
                <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Scansione...</>
              ) : (
                <><Scan className="h-4 w-4 mr-1" /> Avvia Scansione</>
              )}
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="tab-navigation">
          <button
            onClick={() => setActiveTab('anomalies')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'anomalies'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-anomalies"
          >
            <ShieldAlert className="h-4 w-4 inline mr-1.5" />
            Anomalie
            {stats?.total_open > 0 && (
              <Badge className="ml-2 bg-red-500 text-white text-xs">{stats.total_open}</Badge>
            )}
          </button>
          <button
            onClick={() => setActiveTab('compliance')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'compliance'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-compliance"
          >
            <Clock className="h-4 w-4 inline mr-1.5" />
            Compliance 24h
            {complianceStats?.pending_bookings > 0 && (
              <Badge className="ml-2 bg-amber-500 text-white text-xs">{complianceStats.pending_bookings}</Badge>
            )}
          </button>
        </div>

        {activeTab === 'anomalies' && (<>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="anomaly-stats">
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="text-xs text-red-600 font-medium">Dichiarazioni Esterne</p>
                    <p className="text-2xl font-bold text-red-800" data-testid="stat-external">
                      {stats.by_type?.external_declaration?.open || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Doppi Invii</p>
                    <p className="text-2xl font-bold text-amber-800" data-testid="stat-duplicates">
                      {stats.by_type?.duplicate_submission?.open || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-blue-600 font-medium">Ricevute Mancanti</p>
                    <p className="text-2xl font-bold text-blue-800" data-testid="stat-missing">
                      {stats.by_type?.missing_receipt?.open || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-xs text-green-600 font-medium">Risolte</p>
                    <p className="text-2xl font-bold text-green-800" data-testid="stat-resolved">
                      {stats.total_resolved || 0}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Scan Progress */}
        {scanning && scanStatus && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Scansione in corso...</p>
                  <p className="text-sm text-amber-600">
                    Stato: {scanStatus.status}
                    {scanStatus.results?.duplicates && ` | Duplicati: ${scanStatus.results.duplicates.anomalies_created || 0}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Cerca per immobile, ospite, codice..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="anomaly-search-input"
                />
              </div>
              <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-[200px]" data-testid="anomaly-type-filter">
                  <SelectValue placeholder="Tipo anomalia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i tipi</SelectItem>
                  <SelectItem value="external_declaration">Dichiarazioni Esterne</SelectItem>
                  <SelectItem value="duplicate_submission">Doppi Invii</SelectItem>
                  <SelectItem value="missing_receipt">Ricevute Mancanti</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(0); }}>
                <SelectTrigger className="w-full md:w-[160px]" data-testid="anomaly-status-filter">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="open">Aperte</SelectItem>
                  <SelectItem value="resolved">Risolte</SelectItem>
                  <SelectItem value="false_positive">Falsi Positivi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Anomalies Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : filteredAnomalies.length === 0 ? (
              <div className="text-center p-12" data-testid="no-anomalies">
                <Shield className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <p className="text-lg font-medium text-gray-700">Nessuna anomalia trovata</p>
                <p className="text-sm text-gray-500 mt-1">
                  {statusFilter === 'open' ? 'Tutte le anomalie sono state risolte o non ne sono state rilevate.' : 'Nessun risultato per i filtri selezionati.'}
                </p>
                {total === 0 && (
                  <Button
                    onClick={() => setScanDialog(true)}
                    className="mt-4"
                    variant="outline"
                    data-testid="first-scan-btn"
                  >
                    <Scan className="h-4 w-4 mr-2" /> Avvia prima scansione
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="anomalies-table">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Immobile</th>
                      <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Descrizione</th>
                      <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Stato</th>
                      <th className="text-right p-3 text-xs font-medium text-gray-500 uppercase">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnomalies.map((anomaly) => {
                      const TypeIcon = ANOMALY_TYPE_ICONS[anomaly.anomaly_type] || AlertTriangle;
                      return (
                        <tr
                          key={anomaly.id}
                          className="border-b hover:bg-gray-50 transition-colors"
                          data-testid={`anomaly-row-${anomaly.id}`}
                        >
                          <td className="p-3">
                            <Badge
                              className={`${ANOMALY_TYPE_COLORS[anomaly.anomaly_type] || 'bg-gray-100'} text-xs`}
                              variant="outline"
                            >
                              <TypeIcon className="h-3 w-3 mr-1" />
                              {ANOMALY_TYPE_LABELS[anomaly.anomaly_type] || anomaly.anomaly_type}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Building className="h-4 w-4 text-gray-400" />
                              <div>
                                <p className="text-sm font-medium">{anomaly.property_code}</p>
                                <p className="text-xs text-gray-500">{anomaly.property_name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="h-3.5 w-3.5 text-gray-400" />
                              {anomaly.anomaly_date}
                            </div>
                          </td>
                          <td className="p-3 hidden md:table-cell">
                            <p className="text-sm text-gray-700 max-w-md truncate" title={anomaly.description}>
                              {anomaly.description}
                            </p>
                            {anomaly.details?.guest_name && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Ospite: {anomaly.details.guest_name}
                                {anomaly.details.check_in && ` | ${anomaly.details.check_in} - ${anomaly.details.check_out}`}
                              </p>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge className={STATUS_COLORS[anomaly.status] || ''} variant="outline">
                              {STATUS_LABELS[anomaly.status] || anomaly.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right">
                            {anomaly.status === 'open' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedAnomaly(anomaly);
                                  setResolveDialog(true);
                                  setResolutionNote('');
                                  setResolveStatus('resolved');
                                }}
                                data-testid={`resolve-btn-${anomaly.id}`}
                              >
                                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Gestisci
                              </Button>
                            )}
                            {anomaly.status !== 'open' && anomaly.resolution_note && (
                              <span className="text-xs text-gray-500 italic" title={anomaly.resolution_note}>
                                {anomaly.resolution_note.substring(0, 30)}...
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {total} anomalie totali | Pagina {page + 1} di {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        </>)} {/* End anomalies tab */}

        {activeTab === 'compliance' && (
          <div className="space-y-6">
            {/* Compliance Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Compliance Documenti 24h</h2>
                <p className="text-sm text-gray-500">Monitoraggio automatico invio documenti alla Questura post check-in</p>
              </div>
              <Button
                onClick={triggerComplianceCheck}
                disabled={triggeringCheck}
                size="sm"
                variant="outline"
                data-testid="trigger-compliance-btn"
              >
                {triggeringCheck ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                Esegui Controllo
              </Button>
            </div>

            {/* Compliance Timeline Explanation */}
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Timeline automatica post check-in:</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex items-center gap-2 p-2 bg-orange-50 rounded border border-orange-200">
                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    <div>
                      <p className="text-xs font-bold text-orange-800">+10 ore</p>
                      <p className="text-xs text-orange-600">Primo avviso</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <div>
                      <p className="text-xs font-bold text-red-800">+20 ore</p>
                      <p className="text-xs text-red-600">Avviso urgente</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                    <div>
                      <p className="text-xs font-bold text-blue-800">+23 ore</p>
                      <p className="text-xs text-blue-600">Auto-invio Questura</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-gray-900 rounded border border-gray-700">
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                    <div>
                      <p className="text-xs font-bold text-white">+24 ore</p>
                      <p className="text-xs text-gray-300">Escalation ufficio</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Stats */}
            {complianceStats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-amber-600 font-medium">Prenotazioni in Attesa</p>
                    <p className="text-2xl font-bold text-amber-800" data-testid="compliance-pending">
                      {complianceStats.pending_bookings || 0}
                    </p>
                    <p className="text-xs text-amber-500">Check-in recenti senza invio</p>
                  </CardContent>
                </Card>
                <Card className="border-orange-200 bg-orange-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-orange-600 font-medium">Avvisi Inviati</p>
                    <p className="text-2xl font-bold text-orange-800">
                      {(complianceStats.by_action?.reminder_10h || 0) + (complianceStats.by_action?.reminder_20h || 0)}
                    </p>
                    <p className="text-xs text-orange-500">
                      {complianceStats.by_action?.reminder_10h || 0} a 10h + {complianceStats.by_action?.reminder_20h || 0} a 20h
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <p className="text-xs text-red-600 font-medium">Escalation</p>
                    <p className="text-2xl font-bold text-red-800">
                      {(complianceStats.by_action?.auto_send_23h || 0) + (complianceStats.by_action?.escalation_24h || 0)}
                    </p>
                    <p className="text-xs text-red-500">
                      {complianceStats.by_action?.auto_send_23h || 0} auto-invii + {complianceStats.by_action?.escalation_24h || 0} escalation
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Compliance Logs Table */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Storico Azioni Compliance</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {complianceLogs.length === 0 ? (
                  <div className="text-center p-8" data-testid="no-compliance-logs">
                    <Shield className="h-10 w-10 text-green-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">Nessuna azione di compliance registrata</p>
                    <p className="text-xs text-gray-400 mt-1">Il sistema controlla automaticamente ogni ora</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="compliance-table">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Azione</th>
                          <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Immobile</th>
                          <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Inviato a</th>
                          <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Data</th>
                          <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Dettagli</th>
                        </tr>
                      </thead>
                      <tbody>
                        {complianceLogs.map((log, idx) => {
                          const actionConfig = {
                            reminder_10h: { label: 'Avviso 10h', color: 'bg-orange-100 text-orange-800 border-orange-200' },
                            reminder_20h: { label: 'Avviso 20h', color: 'bg-red-100 text-red-800 border-red-200' },
                            auto_send_23h: { label: 'Auto-invio 23h', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                            escalation_24h: { label: 'Escalation 24h', color: 'bg-gray-900 text-white' }
                          };
                          const config = actionConfig[log.action_type] || { label: log.action_type, color: 'bg-gray-100' };
                          return (
                            <tr key={log.id || idx} className="border-b hover:bg-gray-50">
                              <td className="p-3">
                                <Badge className={`${config.color} text-xs`} variant="outline">
                                  {config.label}
                                </Badge>
                              </td>
                              <td className="p-3 text-sm">{log.property_code}</td>
                              <td className="p-3 text-sm text-gray-600">{log.sent_to}</td>
                              <td className="p-3 text-sm text-gray-600">
                                {new Date(log.sent_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td className="p-3 text-xs text-gray-500 hidden md:table-cell">
                                {log.details?.hours_since_checkin && `${log.details.hours_since_checkin}h dal check-in`}
                                {log.details?.auto_sent_to_questura && ' | Inviato a Questura'}
                                {log.details?.cc_office && ' | CC Ufficio'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compliance Pagination */}
            {Math.ceil(complianceTotal / LIMIT) > 1 && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">{complianceTotal} azioni totali</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setCompliancePage(p => Math.max(0, p - 1))} disabled={compliancePage === 0}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setCompliancePage(p => p + 1)} disabled={(compliancePage + 1) * LIMIT >= complianceTotal}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )} {/* End compliance tab */}

        {/* Resolve Dialog */}
        <Dialog open={resolveDialog} onOpenChange={setResolveDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Gestisci Anomalia</DialogTitle>
            </DialogHeader>
            {selectedAnomaly && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                  <Badge className={ANOMALY_TYPE_COLORS[selectedAnomaly.anomaly_type] || ''} variant="outline">
                    {ANOMALY_TYPE_LABELS[selectedAnomaly.anomaly_type]}
                  </Badge>
                  <p className="text-sm font-medium">
                    {selectedAnomaly.property_code} - {selectedAnomaly.property_name}
                  </p>
                  <p className="text-sm text-gray-600">{selectedAnomaly.description}</p>
                  {selectedAnomaly.details?.guest_name && (
                    <p className="text-xs text-gray-500">
                      Ospite: {selectedAnomaly.details.guest_name} |
                      Periodo: {selectedAnomaly.details.check_in} - {selectedAnomaly.details.check_out}
                    </p>
                  )}
                  {selectedAnomaly.details?.submission_count && (
                    <p className="text-xs text-gray-500">
                      Invii totali: {selectedAnomaly.details.submission_count} |
                      Date: {selectedAnomaly.details.submission_dates?.join(', ')}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Azione</label>
                  <Select value={resolveStatus} onValueChange={setResolveStatus}>
                    <SelectTrigger data-testid="resolve-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resolved">Risolta - Problema verificato e gestito</SelectItem>
                      <SelectItem value="false_positive">Falso Positivo - Non e' un'anomalia reale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Note di risoluzione</label>
                  <Input
                    value={resolutionNote}
                    onChange={e => setResolutionNote(e.target.value)}
                    placeholder="Es: Contattato responsabile, confermata prenotazione non registrata..."
                    data-testid="resolution-note-input"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setResolveDialog(false)}>
                    Annulla
                  </Button>
                  <Button
                    onClick={handleResolve}
                    disabled={resolving}
                    data-testid="confirm-resolve-btn"
                  >
                    {resolving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                    Conferma
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Scan Config Dialog */}
        <Dialog open={scanDialog} onOpenChange={setScanDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Configura Scansione Anomalie</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Giorni da analizzare</label>
                <Select value={String(scanDaysBack)} onValueChange={v => setScanDaysBack(Number(v))}>
                  <SelectTrigger data-testid="scan-days-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                    <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                    <SelectItem value="60">Ultimi 60 giorni</SelectItem>
                    <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo di scansione</label>
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={!scanExternal}
                      onChange={() => setScanExternal(false)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium">Analisi Veloce (solo database)</p>
                      <p className="text-xs text-gray-500">
                        Rileva doppi invii e ricevute mancanti. Immediato, senza chiamate esterne.
                      </p>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      checked={scanExternal}
                      onChange={() => setScanExternal(true)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium">Analisi Completa (con scansione Questura)</p>
                      <p className="text-xs text-gray-500">
                        Include controllo dichiarazioni esterne. Richiede tempo (1 sec per giorno per immobile).
                        Rileva se qualcuno ha dichiarato ospiti senza passare dal sistema.
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  {scanExternal
                    ? `La scansione completa potrebbe richiedere diversi minuti. Verra' eseguita in background.`
                    : `L'analisi veloce controlla solo i dati gia' presenti nel database.`
                  }
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setScanDialog(false)}>Annulla</Button>
                <Button
                  onClick={startScan}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-scan-btn"
                >
                  <Scan className="h-4 w-4 mr-1" /> Avvia Scansione
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default QuesturaAnomalyDashboard;
